/**
 * RosterHubPage - Rosters Hub with site cards
 * Hierarchical roster view: Rosters Hub -> Site Roster
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  List,
  MapPin,
  Clock,
  Users,
  AlertTriangle,
  Loader2,
  Building2,
  Filter,
  ArrowUpDown,
  Star,
  StarOff,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Timer
} from 'lucide-react';

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatWeekRange(monday) {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return `${monday.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} - ${sunday.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export default function RosterHubPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api, isAdmin } = useAuth();
  
  // State
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // P0-2 FIX: Always initialize to current week when no explicit weekStart in URL
  // This ensures fresh state on login/sidebar navigation
  const [currentWeek, setCurrentWeek] = useState(() => {
    const weekParam = searchParams.get('weekStart');
    const now = new Date();
    const todayMonday = getMonday(now);
    
    // No weekStart in URL → always use current week (fresh login/sidebar click)
    if (!weekParam) {
      console.log('[RosterHub] No weekStart param, using current week:', todayMonday.toISOString().split('T')[0]);
      return todayMonday;
    }
    
    // Validate the weekStart param
    const parsedDate = new Date(weekParam);
    const year = parsedDate.getFullYear();
    
    // Invalid date format
    if (isNaN(parsedDate.getTime())) {
      console.warn('[RosterHub] Invalid weekStart format:', weekParam);
      return todayMonday;
    }
    
    // Year out of range
    if (year < 2020 || year > 2100) {
      console.warn('[RosterHub] Year out of range:', year);
      return todayMonday;
    }
    
    // P0-2 FIX: Tighter validation - max 8 weeks past, 1 year future
    const maxPast = new Date(now);
    maxPast.setDate(maxPast.getDate() - 56); // 8 weeks back
    const maxFuture = new Date(now);
    maxFuture.setDate(maxFuture.getDate() + 365);
    
    if (parsedDate < maxPast || parsedDate > maxFuture) {
      console.warn('[RosterHub] weekStart out of valid range:', weekParam);
      return todayMonday;
    }
    
    // Valid weekStart, use it
    return getMonday(parsedDate);
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [clientFilter, setClientFilter] = useState(searchParams.get('clientId') || 'all');
  const [regionFilter, setRegionFilter] = useState(searchParams.get('region') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'active');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'name');
  const [viewMode, setViewMode] = useState(localStorage.getItem('rosterHubView') || 'grid');
  
  // Pinned sites (stored in localStorage)
  const [pinnedSites, setPinnedSites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pinnedRosterSites') || '[]');
    } catch {
      return [];
    }
  });

  // Regions from sites
  const regions = [...new Set(sites.map(s => s.region).filter(Boolean))].sort();

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('weekStart', currentWeek.toISOString().split('T')[0]);
    if (search) params.set('search', search);
    if (clientFilter !== 'all') params.set('clientId', clientFilter);
    if (regionFilter !== 'all') params.set('region', regionFilter);
    if (statusFilter !== 'active') params.set('status', statusFilter);
    if (sortBy !== 'name') params.set('sort', sortBy);
    setSearchParams(params, { replace: true });
  }, [currentWeek, search, clientFilter, regionFilter, statusFilter, sortBy, setSearchParams]);

  // Save view mode
  useEffect(() => {
    localStorage.setItem('rosterHubView', viewMode);
  }, [viewMode]);

  // Save pinned sites
  useEffect(() => {
    localStorage.setItem('pinnedRosterSites', JSON.stringify(pinnedSites));
  }, [pinnedSites]);

  // Fetch data
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      const clientList = Array.isArray(response.data)
        ? response.data
        : (response.data?.clients || response.data?.data || []);

      setClients(clientList);
      setSites(clientList);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Navigation
  const handlePreviousWeek = () => {
    const prev = new Date(currentWeek);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeek(prev);
  };

  const handleNextWeek = () => {
    const next = new Date(currentWeek);
    next.setDate(next.getDate() + 7);
    setCurrentWeek(next);
  };

  // P0 FIX: Always use fresh date for Today
  const handleToday = () => {
    const now = new Date();
    const todayMonday = getMonday(now);
    console.log('[RosterHub] TODAY clicked - fresh:', now.toISOString(), '-> Monday:', todayMonday.toISOString().split('T')[0]);
    setCurrentWeek(todayMonday);
  };

  const openSiteRoster = (siteId) => {
    sessionStorage.removeItem('clientRosterManualWeekNav');
    navigate(`/client/${siteId}/roster`);
  };

  const togglePin = (siteId) => {
    if (pinnedSites.includes(siteId)) {
      setPinnedSites(pinnedSites.filter(id => id !== siteId));
    } else {
      setPinnedSites([...pinnedSites, siteId]);
    }
  };

  // Separate pinned and unpinned sites
  const pinnedSitesList = sites.filter(s => pinnedSites.includes(s.id || s._id));
  const unpinnedSitesList = sites.filter(s => !pinnedSites.includes(s.id || s._id));

  // Site card component
  const ClientCard = ({ client, isPinned }) => {
    const site = client || {};
    const hasIssues = ((client?.unfilled_shifts || 0) > 0) || ((site.declined || 0) > 0);
    
    return (
      <Card 
        className={`bg-[#18181b] border-[#3f3f46] hover:border-zinc-500 transition-all cursor-pointer group ${
          isPinned ? 'border-amber-500/30' : ''
        }`}
        onClick={() => openSiteRoster(client.id || client._id)}
        data-testid={`site-card-${client.id || client._id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-white truncate">{client.name || client.company_name || 'Unnamed Client'}</h3>
                {client.site_code && (
                  <Badge className="bg-zinc-700 text-zinc-300 text-xs shrink-0">
                    {client.site_code}
                  </Badge>
                )}
              </div>
              {client.client_name && (
                <p className="text-xs text-zinc-500 truncate mt-0.5">{client.client_name}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 w-7 p-0 ${isPinned ? 'text-amber-400' : 'text-zinc-600 opacity-0 group-hover:opacity-100'}`}
              onClick={(e) => {
                e.stopPropagation();
                togglePin(client.id || client._id);
              }}
            >
              {isPinned ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 bg-[#09090b] rounded">
              <div className="text-lg font-mono font-bold text-zinc-300">{(client.total_shifts || 0)}</div>
              <div className="text-xs text-zinc-500">Shifts</div>
            </div>
            <div className="text-center p-2 bg-[#09090b] rounded">
              <div className={`text-lg font-mono font-bold ${(client.filled_shifts || 0) > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                {(client.filled_shifts || 0)}
              </div>
              <div className="text-xs text-zinc-500">Filled</div>
            </div>
            <div className="text-center p-2 bg-[#09090b] rounded">
              <div className={`text-lg font-mono font-bold ${(client.unfilled_shifts || 0) > 0 ? 'text-orange-400' : 'text-zinc-500'}`}>
                {(client.unfilled_shifts || 0)}
              </div>
              <div className="text-xs text-zinc-500">Unfilled</div>
            </div>
          </div>

          {/* Hours & Status */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1 text-zinc-400">
              <Timer className="w-3.5 h-3.5" />
              {(client.hours || 0)}h
            </div>
            <div className="flex items-center gap-2">
              {(client.accepted || 0) > 0 && (
                <span className="flex items-center gap-1 text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  {(client.accepted || 0)}
                </span>
              )}
              {(client.pending || 0) > 0 && (
                <span className="flex items-center gap-1 text-blue-400">
                  <Clock className="w-3 h-3" />
                  {(client.pending || 0)}
                </span>
              )}
              {(site.declined || 0) > 0 && (
                <span className="flex items-center gap-1 text-red-400">
                  <XCircle className="w-3 h-3" />
                  {site.declined || 0}
                </span>
              )}
            </div>
          </div>

          {/* Warnings */}
          {hasIssues && (
            <div className="flex items-center gap-1 mt-2 text-xs text-amber-400">
              <AlertTriangle className="w-3 h-3" />
              {(client.unfilled_shifts || 0) > 0 && `${(client.unfilled_shifts || 0)} unfilled`}
              {(client.unfilled_shifts || 0) > 0 && (site.declined || 0) > 0 && ' • '}
              {(site.declined || 0) > 0 && `${site.declined || 0} declined`}
            </div>
          )}

          {/* Open Button */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-3 h-8 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
            onClick={(e) => {
              e.stopPropagation();
              openSiteRoster(client.id || client._id);
            }}
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            Open Roster
          </Button>
        </CardContent>
      </Card>
    );
  };

  // List row component
  const SiteRow = ({ client, isPinned }) => (
    <div 
      className={`flex items-center gap-4 p-3 bg-[#18181b] border border-[#3f3f46] rounded-lg hover:border-zinc-500 transition-all cursor-pointer group ${
        isPinned ? 'border-amber-500/30' : ''
      }`}
      onClick={() => openSiteRoster(client.id || client._id)}
      data-testid={`site-row-${client.id || client._id}`}
    >
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 shrink-0 ${isPinned ? 'text-amber-400' : 'text-zinc-600 opacity-0 group-hover:opacity-100'}`}
        onClick={(e) => {
          e.stopPropagation();
          togglePin(client.id || client._id);
        }}
      >
        {isPinned ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
      </Button>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white truncate">{client.name || client.company_name || 'Unnamed Client'}</span>
          {client.site_code && (
            <Badge className="bg-zinc-700 text-zinc-300 text-xs">{client.site_code}</Badge>
          )}
        </div>
        {client.client_name && (
          <p className="text-xs text-zinc-500 truncate">{client.client_name}</p>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm shrink-0">
        <div className="text-center min-w-[60px]">
          <div className="font-mono text-zinc-300">{(client.total_shifts || 0)}</div>
          <div className="text-xs text-zinc-500">Shifts</div>
        </div>
        <div className="text-center min-w-[50px]">
          <div className={`font-mono ${(client.filled_shifts || 0) > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
            {(client.filled_shifts || 0)}
          </div>
          <div className="text-xs text-zinc-500">Filled</div>
        </div>
        <div className="text-center min-w-[50px]">
          <div className={`font-mono ${(client.unfilled_shifts || 0) > 0 ? 'text-orange-400' : 'text-zinc-500'}`}>
            {(client.unfilled_shifts || 0)}
          </div>
          <div className="text-xs text-zinc-500">Unfilled</div>
        </div>
        <div className="text-center min-w-[50px]">
          <div className="font-mono text-zinc-400">{(client.hours || 0)}h</div>
          <div className="text-xs text-zinc-500">Hours</div>
        </div>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 text-blue-400 hover:text-blue-300"
      >
        <ExternalLink className="w-4 h-4" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="roster-hub-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            ROSTERS
          </h1>
          <p className="text-sm text-zinc-500">
            {total} site{total !== 1 ? 's' : ''} • w/c {formatWeekRange(currentWeek)}
          </p>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-[#18181b] border border-[#3f3f46] rounded-sm">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handlePreviousWeek}
              className="h-9 px-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToday}
              className="h-9 px-3 text-xs"
            >
              Today
            </Button>
            <span className="text-sm text-zinc-300 px-2 whitespace-nowrap">
              {currentWeek.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleNextWeek}
              className="h-9 px-2"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search sites..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#18181b] border-[#3f3f46] h-9"
            data-testid="site-search"
          />
        </div>

        {/* Client Filter */}
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[160px] bg-[#18181b] border-[#3f3f46] h-9" data-testid="client-filter">
            <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="All Clients" />
          </SelectTrigger>
          <SelectContent className="bg-[#18181b] border-[#3f3f46]">
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(client => (
              <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Region Filter */}
        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-[140px] bg-[#18181b] border-[#3f3f46] h-9" data-testid="region-filter">
            <MapPin className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="All Regions" />
          </SelectTrigger>
          <SelectContent className="bg-[#18181b] border-[#3f3f46]">
            <SelectItem value="all">All Regions</SelectItem>
            {regions.map(region => (
              <SelectItem key={region} value={region}>{region}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[120px] bg-[#18181b] border-[#3f3f46] h-9" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#18181b] border-[#3f3f46]">
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px] bg-[#18181b] border-[#3f3f46] h-9" data-testid="sort-select">
            <ArrowUpDown className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#18181b] border-[#3f3f46]">
            <SelectItem value="name">A-Z</SelectItem>
            <SelectItem value="shifts">Most Shifts</SelectItem>
            <SelectItem value="hours">Most Hours</SelectItem>
            <SelectItem value="updated">Recently Updated</SelectItem>
          </SelectContent>
        </Select>

        {/* View Toggle */}
        <div className="flex items-center bg-[#18181b] border border-[#3f3f46] rounded-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode('grid')}
            className={`h-9 px-3 ${viewMode === 'grid' ? 'text-blue-400' : 'text-zinc-500'}`}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode('list')}
            className={`h-9 px-3 ${viewMode === 'list' ? 'text-blue-400' : 'text-zinc-500'}`}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : sites.length === 0 ? (
        /* Empty State */
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Calendar className="w-12 h-12 text-zinc-600 mb-4" />
            <p className="text-zinc-400 text-center">No sites match your filters</p>
            <p className="text-zinc-600 text-sm text-center mt-1">
              Try adjusting your search or filters
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4 border-zinc-600"
              onClick={() => {
                setSearch('');
                setClientFilter('all');
                setRegionFilter('all');
                setStatusFilter('active');
              }}
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Pinned Sites */}
          {pinnedSitesList.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-amber-400 flex items-center gap-2">
                <Star className="w-4 h-4 fill-current" />
                PINNED CLIENTS ({pinnedSitesList.length})
              </h2>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {pinnedSitesList.map(client => (
                    <ClientCard key={client.id || client._id} client={client} isPinned />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {pinnedSitesList.map(client => (
                    <SiteRow key={client.id || client._id} client={client} isPinned />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All Sites */}
          <div className="space-y-3">
            {pinnedSitesList.length > 0 && (
              <h2 className="text-sm font-medium text-zinc-400">
                ALL CLIENTS ({unpinnedSitesList.length})
              </h2>
            )}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {unpinnedSitesList.map(client => (
                  <ClientCard key={client.id || client._id} client={client} isPinned={false} />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {unpinnedSitesList.map(client => (
                  <SiteRow key={client.id || client._id} client={client} isPinned={false} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
